import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import EMPAccordion from "../../../shared/components/EMPAccordion/EMPAccordion";
import EMPBanner from "../../../shared/components/EMPBanner/EMPBanner";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import MotionChevronRightCircle from "../../../shared/Icons/MotionChevronRightCircle";
import accordionStyles from "../../../shared/components/EMPAccordion/EMPAccordionLincoln.module.scss";
import OnboardingPageTitle from "../../../shared/components/OnboardingPageTitle/OnboardingPageTitle";
import TermsAndConditions from "../../TermsAndConditions/TermsAndConditions";
import DealerInformation from "../../DealerInformation/DealerInformation";
import InDealershipTeamSetup from "../../InDealershipTeamSetup";
import DealerOnboardingClient from "../../../clients/DealerOnboardingClient.js";
import {
  DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
  ENROLLMENT_SUBSTEP_DPES,
  ENROLLMENT_SUBSTEP_DEALERSHIP_INFO,
  ENROLLMENT_SUBSTEP_IN_DEALERSHIP_TEAM,
  findEnrollmentSubstep,
  findEnrollmentTermsSubstep,
  findEnrollmentTermsSubstepIds,
  formatTermsAcceptedDate,
} from "../../../shared/utils/onboardingProgressUtil";
import { resolveEnrollmentTermsType } from "../../../utils/enrollmentTermsUtil";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";
import styles from "./OnboardingEnrollment.module.scss";

const TERMS_CONFIRMATION_TIMEOUT_MS = 5000;

const getAcceptanceInfoForUi = (termsSubstep) => {
  if (termsSubstep?.status !== "completed") {
    return null;
  }

  return {
    date: formatTermsAcceptedDate(termsSubstep?.completedAt),
    cdsid: termsSubstep?.updatedBy ? String(termsSubstep.updatedBy).trim() : "",
  };
};

const getCompletionIconType = (completed) => (completed ? "check" : "caution");

const normalizeStatus = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const formatDpesUnenrolledDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const OnboardingEnrollment = ({ user, progressRequest = null }) => {
  const { t } = useTranslation();
  const [expandedDpesEnrollment, setExpandedDpesEnrollment] = useState(false);
  const [expandedTerms, setExpandedTerms] = useState(false);
  const [expandedDealerInformation, setExpandedDealerInformation] =
    useState(false);
  const [expandedTeamSetup, setExpandedTeamSetup] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  const [showTermsConfirmation, setShowTermsConfirmation] = useState(false);
  const termsConfirmationTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (termsConfirmationTimeoutRef.current) {
        clearTimeout(termsConfirmationTimeoutRef.current);
      }
    },
    []
  );

  const queryClient = useQueryClient();
  const progressQueryKey = [
    DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
    progressRequest?.dealerCommonId,
  ];

  const {
    data: progressData,
    isLoading: progressLoading,
    isError: progressQueryError,
  } = useQuery({
    queryKey: progressQueryKey,
    queryFn: () =>
      DealerOnboardingClient.getDealerOnboardingProgress(
        progressRequest.dealerCommonId,
        progressRequest.dealerInfo
      ),
    enabled: !!progressRequest,
    select: (response) => response?.data ?? null,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const steps = progressData?.steps;
  const termsSubstepIds = steps ? findEnrollmentTermsSubstepIds(steps) : null;

  const termsSubstep = findEnrollmentTermsSubstep(steps);
  const termsCompleted = termsSubstep?.status === "completed";

  const dpesSubstep = findEnrollmentSubstep(steps, ENROLLMENT_SUBSTEP_DPES);
  const dpesCompleted = normalizeStatus(dpesSubstep?.status) === "completed";
  const dpesUnenrolled = [
    dpesSubstep?.status,
    dpesSubstep?.externalVerificationStatus,
    dpesSubstep?.payloadJson?.enrollmentStatus,
  ]
    .map(normalizeStatus)
    .some((status) => status === "canceled" || status === "terminated");
  const dpesUnenrolledDate = formatDpesUnenrolledDate(
    dpesSubstep?.payloadJson?.unenrolledAt ??
      dpesSubstep?.payloadJson?.dateUpdated
  );

  const dealershipInfoCompleted =
    findEnrollmentSubstep(steps, ENROLLMENT_SUBSTEP_DEALERSHIP_INFO)?.status ===
    "completed";
  const inDealershipTeamCompleted =
    findEnrollmentSubstep(steps, ENROLLMENT_SUBSTEP_IN_DEALERSHIP_TEAM)
      ?.status === "completed";

  const acceptanceInfoForUi = getAcceptanceInfoForUi(termsSubstep);
  const updatedBy = String(user?.userId ?? "").trim();

  const enrollmentTermsType = useMemo(
    () =>
      resolveEnrollmentTermsType({
        brand: user?.brand,
        dualDealer: progressData?.dealer?.dualDealer === true,
      }),
    [user?.brand, progressData?.dealer?.dualDealer]
  );

  const configError =
    !progressLoading && !progressQueryError && progressData && !termsSubstepIds;

  const userIdError = Boolean(progressRequest && !updatedBy && !termsCompleted);

  const saveMutation = useMutation({
    mutationFn: () => {
      setAcceptError(null);
      return DealerOnboardingClient.acceptEnrollmentTerms(
        progressRequest.dealerCommonId,
        { updatedBy, termsType: enrollmentTermsType }
      );
    },
    onSuccess: () => {
      setShowTermsConfirmation(true);
      if (termsConfirmationTimeoutRef.current) {
        clearTimeout(termsConfirmationTimeoutRef.current);
      }
      termsConfirmationTimeoutRef.current = setTimeout(() => {
        setShowTermsConfirmation(false);
      }, TERMS_CONFIRMATION_TIMEOUT_MS);
      queryClient.invalidateQueries({
        queryKey: progressQueryKey,
        exact: true,
        refetchType: "active",
      });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const message = err?.response?.data?.message;
      const raw = detail || message || err?.message || "save_failed";
      setAcceptError(typeof raw === "string" ? raw : "save_failed");
    },
  });

  const acceptBlocked =
    !progressRequest ||
    progressLoading ||
    progressQueryError ||
    !termsSubstepIds ||
    (!updatedBy && !termsCompleted) ||
    termsCompleted ||
    saveMutation.isSuccess;

  const handleDpesEnrollmentAccordionChange = () => {
    setExpandedDpesEnrollment(!expandedDpesEnrollment);
  };

  const handleTermsAccordionChange = () => {
    setExpandedTerms(!expandedTerms);
  };

  const handleAccordionChangeDealerInformation = () => {
    setExpandedDealerInformation(!expandedDealerInformation);
  };

  const handleTeamSetupAccordionChange = () => {
    setExpandedTeamSetup(!expandedTeamSetup);
  };

  const handleTermsAccept = () => {
    if (acceptBlocked || saveMutation.isPending) {
      return;
    }
    saveMutation.mutate();
  };

  const enrichedDealerInfo = useMemo(() => {
    if (!progressRequest?.dealerInfo) return undefined;
    const phone = progressData?.dealer?.phoneNumber;
    if (!phone) return progressRequest.dealerInfo;
    return { ...progressRequest.dealerInfo, phoneNumber: phone };
  }, [progressRequest?.dealerInfo, progressData?.dealer?.phoneNumber]);

  const isFordBrand = IS_FORD_BRAND(user?.brand);
  const brandName = isFordBrand ? "Ford" : "Lincoln";

  const dpesButtonClassName = isFordBrand
    ? styles.dpesButtonFord
    : styles.dpesButtonLincoln;

  const dpesButtonIconClassName = isFordBrand
    ? styles.dpesButtonIconFord
    : styles.dpesButtonIconLincoln;

  return (
    <Box className="tw-bg-white tw-rounded-lg tw-p-8">
      <OnboardingPageTitle testId="enrollment-page-title" className="tw-mb-6">
        {t("DealerOnboarding.content.onboardingSteps.enrollment.title")}
      </OnboardingPageTitle>
      <Box data-testid="onboarding-enrollment">
        {progressQueryError && (
          <Typography
            color="error"
            variant="body2"
            className="tw-mb-4"
            data-testid="onboarding-progress-error"
          >
            {t(
              "DealerOnboarding.content.termsAndConditions.progressLoadError",
              {
                defaultValue:
                  "Could not load onboarding progress. Please refresh and try again.",
              }
            )}
          </Typography>
        )}
        {configError && (
          <Typography
            color="error"
            variant="body2"
            className="tw-mb-4"
            data-testid="onboarding-terms-config-error"
          >
            {t(
              "DealerOnboarding.content.termsAndConditions.missingSubstepConfig",
              {
                defaultValue:
                  "Terms step is not available. Please contact support.",
              }
            )}
          </Typography>
        )}

        {userIdError && (
          <Typography
            color="error"
            variant="body2"
            className="tw-mb-4"
            data-testid="onboarding-user-id-error"
          >
            {t("DealerOnboarding.content.termsAndConditions.missingUserId", {
              defaultValue:
                "Your account is missing a user id; acceptance cannot be saved.",
            })}
          </Typography>
        )}
        {acceptError && (
          <Typography
            color="error"
            variant="body2"
            className="tw-mb-4"
            data-testid="onboarding-accept-save-error"
          >
            {t("DealerOnboarding.content.termsAndConditions.acceptSaveError", {
              defaultValue:
                "Could not save acceptance. Please try again or refresh the page.",
            })}
          </Typography>
        )}
        <EMPAccordion
          heading={t("DealerOnboarding.content.dpesEnrollment.title")}
          headingClassName={accordionStyles.accordionHeading}
          expanded={expandedDpesEnrollment}
          onChange={handleDpesEnrollmentAccordionChange}
          prefixIconType={getCompletionIconType(dpesCompleted)}
          toggleIconColor="grey"
        >
          {dpesUnenrolled && (
            <Box
              className={styles.dpesWarningBanner}
              data-testid="dpes-unenrolled-warning-banner"
            >
              <EMPBanner
                severity="error"
                title={t(
                  "DealerOnboarding.content.dpesEnrollment.unenrolledBanner.title",
                  {
                    currentBrand: brandName,
                    timestamp: dpesUnenrolledDate,
                  }
                )}
                content={t(
                  "DealerOnboarding.content.dpesEnrollment.unenrolledBanner.description"
                )}
              />
            </Box>
          )}

          <Typography variant="body1" className={styles.dpesBody}>
            {dpesCompleted ? (
              t("DealerOnboarding.content.dpesEnrollment.completedMessage", {
                currentBrand: brandName,
              })
            ) : (
              <>
                {t("DealerOnboarding.content.dpesEnrollment.description", {
                  currentBrand: brandName,
                })}
                <br />

                <span className={styles.dpesStatusMessage}>
                  {t(
                    "DealerOnboarding.content.dpesEnrollment.statusUpdateMessage"
                  )}
                </span>
              </>
            )}
          </Typography>

          {!dpesCompleted && (
            <Box sx={{ mt: "32px" }}>
              <EMPButton
                className={dpesButtonClassName}
                variant="contained"
                href="https://www.dpesng.dealerconnection.com/"
                target="_blank"
                rel="noopener noreferrer"
                endIcon={
                  <MotionChevronRightCircle
                    className={dpesButtonIconClassName}
                  />
                }
                testId="go-to-dpes-button"
              >
                {t("DealerOnboarding.content.dpesEnrollment.buttonLabel")}
              </EMPButton>
            </Box>
          )}
        </EMPAccordion>
        <EMPAccordion
          heading={t("DealerOnboarding.content.termsAndConditions.title")}
          headingClassName={accordionStyles.accordionHeading}
          expanded={expandedTerms}
          onChange={handleTermsAccordionChange}
          prefixIconType={getCompletionIconType(termsCompleted)}
          toggleIconColor="grey"
        >
          <TermsAndConditions
            onAccept={handleTermsAccept}
            termsType={enrollmentTermsType}
            termsAccepted={termsCompleted}
            acceptanceInfo={acceptanceInfoForUi}
            acceptBlocked={acceptBlocked}
            saveInProgress={saveMutation.isPending}
            showConfirmation={showTermsConfirmation}
            onConfirmationClose={() => setShowTermsConfirmation(false)}
          />
        </EMPAccordion>
        <EMPAccordion
          heading={t("DealerOnboarding.content.dealerInformation.title")}
          headingClassName={accordionStyles.accordionHeading}
          expanded={expandedDealerInformation}
          onChange={handleAccordionChangeDealerInformation}
          prefixIconType={getCompletionIconType(dealershipInfoCompleted)}
          toggleIconColor="grey"
        >
          <DealerInformation dealerInfo={enrichedDealerInfo} />
        </EMPAccordion>
        <EMPAccordion
          heading="In-Dealership Team Setup"
          headingClassName={accordionStyles.accordionHeading}
          expanded={expandedTeamSetup}
          onChange={handleTeamSetupAccordionChange}
          prefixIconType={getCompletionIconType(inDealershipTeamCompleted)}
          toggleIconColor="grey"
        >
          <InDealershipTeamSetup />
        </EMPAccordion>
      </Box>
    </Box>
  );
};

OnboardingEnrollment.propTypes = {
  user: PropTypes.object,
  progressRequest: PropTypes.shape({
    dealerCommonId: PropTypes.string.isRequired,
    dealerInfo: PropTypes.object,
  }),
};

export default OnboardingEnrollment;